import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  GraduationCap,
  BookOpen,
  Briefcase,
  Building2,
  DollarSign,
  Calendar,
  Shield,
  Clock,
  Globe2,
  Users,
  Award,
  TrendingUp,
  Mail,
  MessageCircle,
  ChevronRight,
  Star,
  AlertCircle,
  MapPin,
  Heart,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { usePageMeta, useServiceSchema, useFAQSchema } from '../../hooks/useSEO';
import BreadcrumbSchema from '../../components/seo/BreadcrumbSchema';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';

/* ────────────────────────────────────────────────────────────────── */
/* DATA                                                                */
/* ────────────────────────────────────────────────────────────────── */

const keyReasons = [
  { factor: 'Education', details: '5 universities in the global Top 100 (UofT, UBC, McGill, Waterloo, McMaster).', icon: Award },
  { factor: 'Affordability', details: '₹10–25 lakhs/year tuition — significantly cheaper than USA and UK.', icon: DollarSign },
  { factor: 'Work Permit', details: 'Up to 3-year PGWP with no job offer required after graduation.', icon: Briefcase },
  { factor: 'PR Pathway', details: 'Express Entry & PNP — the clearest, most predictable PR system globally.', icon: Shield },
  { factor: 'Family Friendly', details: 'Spouse Open Work Permit + free public schooling for dependent children.', icon: Heart },
  { factor: 'Multicultural & Safe', details: 'Top-10 globally for quality of life, safety, and diversity.', icon: Globe2 },
];

const topCourses = [
  {
    category: 'STEM',
    title: 'CS, AI & Data Science',
    salary: 'CAD 60,000 – 80,000',
    desc: 'Top programs at Waterloo, UofT, UBC. Massive demand in fintech, AI startups, and Big Tech (Shopify, Microsoft, Amazon).',
    icon: TrendingUp,
    link: '/study-abroad/careers/canada/best-courses-for-pr/',
    linkLabel: 'Best STEM Courses for PR',
    accent: 'from-red-600/10 to-red-500/5 border-red-200',
  },
  {
    category: 'Healthcare',
    title: 'Nursing & Public Health',
    salary: 'CAD 55,000 – 90,000',
    desc: 'Highest PR demand. Healthcare-Targeted Express Entry draws prioritize nurses, pharmacists, and lab technicians.',
    icon: Shield,
    link: '/study-abroad/careers/canada/pr-courses/',
    linkLabel: 'Top PR Courses',
    accent: 'from-[#003087]/10 to-[#003087]/5 border-[#003087]/30',
  },
  {
    category: 'Business',
    title: 'MBA & Project Management',
    salary: 'CAD 70,000 – 100,000',
    desc: 'Rotman, Ivey, Schulich. Project Management Graduate Certificates from Humber & Conestoga are PR-magnet programs.',
    icon: Briefcase,
    link: '/study-abroad/comparison/canada-vs-uk-for-pr/',
    linkLabel: 'Canada vs UK for PR',
    accent: 'from-[#C5A028]/10 to-[#C5A028]/5 border-[#C5A028]/40',
  },
];

const topUniversities = {
  tier1: [
    { name: 'University of Toronto', city: 'Toronto, ON', rank: '#1 in Canada' },
    { name: 'University of British Columbia', city: 'Vancouver, BC', rank: 'Top 40 Global' },
    { name: 'McGill University', city: 'Montréal, QC', rank: 'Top 50 Global' },
    { name: 'University of Waterloo', city: 'Waterloo, ON', rank: '#1 for Tech / AI' },
  ],
  tier2: [
    { name: 'University of Alberta', city: 'Edmonton, AB', rank: 'Top Research' },
    { name: 'University of Ottawa', city: 'Ottawa, ON', rank: 'Government & Policy' },
    { name: 'York University', city: 'Toronto, ON', rank: 'Schulich MBA' },
    { name: 'University of Calgary', city: 'Calgary, AB', rank: 'Energy & STEM' },
  ],
  colleges: [
    { name: 'Seneca College', city: 'Toronto, ON', rank: 'PGWP Eligible' },
    { name: 'Humber College', city: 'Toronto, ON', rank: 'Top Business Co-op' },
    { name: 'Georgian College', city: 'Barrie, ON', rank: 'PR-Focused Certs' },
    { name: 'Conestoga College', city: 'Kitchener, ON', rank: 'Highest PR Conversion' },
  ],
};

const visaSteps = [
  { n: 1, title: 'University / College Application', desc: 'Shortlist DLI-listed institutions, submit applications with SOP, LORs, transcripts and IELTS/PTE scores.' },
  { n: 2, title: 'Letter of Acceptance (LOA)', desc: 'Receive your conditional or unconditional LOA from a Designated Learning Institution (DLI).' },
  { n: 3, title: 'GIC + Tuition Deposit', desc: 'Pay one year of tuition + purchase GIC of CAD 20,635 (from Scotiabank, ICICI, RBC, SBI, etc.).' },
  { n: 4, title: 'Study Permit Application', desc: 'Apply online via IRCC portal under SDS (Student Direct Stream) or non-SDS. Pay CAD 150 fee + biometrics CAD 85.' },
  { n: 5, title: 'Biometrics & Medical', desc: 'Visit VFS Global for biometrics and panel physician for upfront medical examination.' },
  { n: 6, title: 'Decision & Departure', desc: 'SDS decisions within 4–6 weeks. Receive Port of Entry (POE) Letter — your Study Permit is stamped on arrival in Canada.' },
];

const faqs = [
  {
    q: 'Is Canada still a good study destination for Indian students in 2026?',
    a: 'Absolutely. Despite the 2024 study-permit cap, Canada remains the #1 destination for Indian students. The 3-year PGWP and Express Entry PR pathway remain unchanged for master\'s and PGWP-eligible college programs.',
  },
  {
    q: 'How much GIC is required for a Canada student visa in 2026?',
    a: 'The Government of Canada raised the Cost of Living requirement to CAD 20,635 (~₹12.5 Lakhs) for a single applicant in January 2024 — this is the minimum GIC you must purchase. This is separate from your tuition fee.',
  },
  {
    q: 'What is the difference between SDS and non-SDS Canada visa?',
    a: 'SDS (Student Direct Stream) is a fast-track route (4–6 weeks) for Indian students who meet specific criteria: IELTS 6.0+ in each band (or PTE 60+, CELPIP 7+), GIC purchase, and first-year tuition paid. Non-SDS takes 8–12 weeks and is for applicants who don\'t meet SDS requirements.',
  },
  {
    q: 'What is the minimum IELTS score required for Canada study visa?',
    a: 'For SDS: IELTS 6.0 with no band below 6.0 (or equivalent PTE 60+ / CELPIP 7+). For non-SDS: 6.5 overall with no band below 6.0 is generally accepted. Master\'s programs often require 6.5–7.0 overall.',
  },
  {
    q: 'What are the new rules for Canada study visa in 2024-2026?',
    a: '(1) GIC increased to CAD 20,635. (2) Study-permit cap reduced for undergrad/college applicants. (3) Spouse Open Work Permit now restricted to spouses of master\'s, doctoral, and select professional programs. (4) PGWP for some private-public partnership colleges restricted. Master\'s + PGWP-eligible colleges remain protected.',
  },
  {
    q: 'How long is the Post-Graduation Work Permit (PGWP) in Canada?',
    a: 'PGWP length matches your program length up to 3 years maximum. A 2-year master\'s = 3-year PGWP. A 1-year graduate certificate = 1-year PGWP. PGWP is open work permit — no job offer required, work for any employer in Canada.',
  },
  {
    q: 'What is the financial proof required for Canada student visa?',
    a: 'You must show: (a) GIC of CAD 20,635 to cover living expenses for the first year, AND (b) Proof of first-year tuition paid in full. Optionally, an education loan letter or 4-month bank statement showing additional funds strengthens your application.',
  },
  {
    q: 'Can I bring my family / spouse to Canada on a student visa?',
    a: 'Yes — your spouse can apply for an Open Work Permit (SOWP) IF you are enrolled in a master\'s, doctoral, or select professional program (medicine, dentistry, law, engineering, pharmacy, etc.). Dependent children get free public schooling.',
  },
  {
    q: 'What is Express Entry and how does it help me get PR?',
    a: 'Express Entry is Canada\'s online PR application system. After completing your studies + 1 year of Canadian work experience via PGWP, you can apply through the Canadian Experience Class (CEC) and receive PR in 6–8 months.',
  },
  {
    q: 'What is PNP (Provincial Nominee Program)?',
    a: 'PNP is a province-specific PR pathway. Each province (Ontario, BC, Alberta, Saskatchewan, etc.) nominates candidates based on their labour market needs. Combined with Express Entry, PNP boosts your CRS score by 600 points — almost guaranteeing PR.',
  },
  {
    q: 'How long does Canada student visa take in 2026?',
    a: 'SDS route: 4–6 weeks for Indian applicants. Non-SDS: 8–12 weeks. Apply at least 12 weeks before your program intake to avoid delays.',
  },
  {
    q: 'Can I work while studying in Canada?',
    a: 'Yes. International students can work up to 24 hours/week off-campus during academic sessions (raised from 20 hours in late 2024) and unlimited on-campus. Full-time work allowed during scheduled breaks.',
  },
  {
    q: 'Which is better — Canadian University or College?',
    a: 'Universities offer Bachelor\'s, Master\'s, and Doctoral degrees with research focus. Colleges offer 1–2 year graduate certificates / diplomas that are PGWP-eligible AND highly career-focused — often the FASTEST PR pathway for working professionals.',
  },
  {
    q: 'What are the intakes for Canadian universities?',
    a: 'Three intakes: Fall (September) — biggest with 80% of programs available; Winter (January) — mid-year intake; Summer (May) — limited programs at select colleges and universities.',
  },
  {
    q: 'Can I switch from a college / university after starting my studies?',
    a: 'Yes, you can transfer between DLIs without applying for a new study permit. However, you must report the transfer to IRCC within 90 days. Transferring to a non-DLI institution voids your study permit.',
  },
  {
    q: 'What are the best courses for getting PR in Canada?',
    a: 'High-PR demand courses: (1) Nursing & Public Health, (2) Computer Science / Data Science / AI, (3) Project Management, (4) Supply Chain & Logistics, (5) Early Childhood Education, (6) Trades (welding, electrician, plumbing). Healthcare and STEM are prioritized in category-based Express Entry draws.',
  },
  {
    q: 'Are there scholarships for Indian students in Canada?',
    a: 'Yes. Major scholarships: Vanier CGS (CAD 50,000/year for PhD), Lester B. Pearson (full-ride at UofT), Trudeau Foundation, plus university-specific awards at McGill, UBC, Waterloo, Queens, and Western.',
  },
  {
    q: 'Will the 2024 study permit cap affect my application?',
    a: 'The cap affects only undergraduate and college diploma applicants. Master\'s, PhD, and PGWP-eligible programs at recognized DLIs are EXEMPT. Indian students applying for master\'s in 2026 will not be impacted.',
  },
  {
    q: 'What is the average cost of living in Canada for Indian students?',
    a: 'Toronto / Vancouver: CAD 18,000–22,000/year. Mid-tier cities (Ottawa, Calgary, Edmonton): CAD 14,000–18,000/year. Smaller cities (Halifax, Winnipeg, Saskatoon): CAD 10,000–14,000/year.',
  },
  {
    q: 'Is health insurance covered for international students in Canada?',
    a: 'Provincial health coverage varies. Most provinces (BC, AB, SK, MB, NL, NB) provide free public healthcare to international students. Ontario and Quebec require private insurance (~CAD 600–900/year) — usually arranged by your institution.',
  },
];

const stats = [
  { val: '180K+', label: 'Indian Students' },
  { val: '5', label: 'Top-100 Universities' },
  { val: 'CAD 20,635', label: '2024 GIC Requirement' },
  { val: '3 Years', label: 'Max PGWP Duration' },
];

const quickLinks = [
  { label: 'Canada Student Visa', path: '/study-abroad/canada/student-visa/', icon: Shield },
  { label: 'SDS Visa Stream', path: '/study-abroad/canada/sds-visa/', icon: Shield },
  { label: 'Top Universities', path: '/study-abroad/universities/canada/', icon: Building2 },
  { label: 'Cost of Study', path: '/study-abroad/canada/cost-of-study/', icon: DollarSign },
  { label: 'Scholarships', path: '/study-abroad/canada/scholarships/', icon: GraduationCap },
  { label: 'PR After Study', path: '/study-abroad/canada/pr-after-study/', icon: CheckCircle2 },
  { label: 'PR Courses', path: '/study-abroad/careers/canada/pr-courses/', icon: BookOpen },
  { label: 'Best Courses for PR', path: '/study-abroad/careers/canada/best-courses-for-pr/', icon: Award },
  { label: 'PGWP Guide', path: '/study-abroad/careers/canada/pr-after-study/', icon: Briefcase },
  { label: 'Express Entry', path: '/study-abroad/canada/express-entry/', icon: TrendingUp },
  { label: 'PNP Programs', path: '/study-abroad/canada/pnp/', icon: MapPin },
  { label: 'Canada Intakes', path: '/study-abroad/canada/intakes/', icon: Calendar },
];

/* High-quality, freely-licensed Canada imagery (Unsplash CDN). */
const IMG_TORONTO =
  'https://images.unsplash.com/photo-1517090504586-fde19ea6066f?auto=format&fit=crop&w=1600&q=70';
const IMG_VANCOUVER =
  'https://images.unsplash.com/photo-1519832979-6fa011b87667?auto=format&fit=crop&w=1600&q=70';
const IMG_MONTREAL =
  'https://images.unsplash.com/photo-1519178614-68673b201f36?auto=format&fit=crop&w=1600&q=70';
const IMG_CAMPUS =
  'https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=1600&q=70';

/* ────────────────────────────────────────────────────────────────── */
/* COMPONENT                                                           */
/* ────────────────────────────────────────────────────────────────── */

const CanadaHubPage = () => {
  const [whatsAppHover, setWhatsAppHover] = useState(false);

  usePageMeta(
    'Study in Canada 2026: Complete Guide for Indian Students | GIC, PGWP, PR',
    "Complete 2026 guide to study in Canada for Indian students. Updated GIC ₹12.5 L, 3-year PGWP, Express Entry & PNP PR pathways, IELTS 6.0+, top universities & colleges, SDS visa, and costs ₹21–93 L. Free counselling with Zafco.",
  );

  useServiceSchema({
    name: 'Canada Study Abroad Consulting for Indian Students',
    description:
      'End-to-end Canada study-abroad services: SDS visa filing, university & college shortlisting, GIC + tuition coordination, PGWP planning, Express Entry & PNP pathway guidance for Indian students.',
    serviceType: 'Education Consulting',
    areaServed: ['Canada', 'India'],
    audience: 'Indian students pursuing higher education and PR in Canada',
    url: 'https://zafco.in/study-abroad/canada/',
  });

  useFAQSchema(faqs);

  return (
    <div className="bg-white" data-testid="sa-canada-hub-page">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://zafco.in/' },
          { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
          { name: 'Study in Canada', url: 'https://zafco.in/study-abroad/canada/' },
        ]}
      />

      {/* ============================ HERO ============================ */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 55%, #7A0E1A 100%)' }}
        data-testid="canada-hero"
      >
        <div className="absolute inset-0 opacity-[0.18]">
          <img
            src={IMG_TORONTO}
            alt="Toronto skyline at dusk featuring CN Tower"
            loading="eager"
            width={1600}
            height={900}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/95 via-[#003087]/80 to-transparent" />
        <div className="absolute top-10 right-[-80px] w-[420px] h-[420px] bg-[#FF1F2D]/25 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-slate-300/90">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/study-abroad/" className="hover:text-white">Study Abroad</Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-[#C5A028] font-semibold">Canada</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-lg" aria-hidden="true">{getFlagEmoji('CA')}</span>
              <span className="text-[11px] text-white/95 font-bold tracking-[0.18em] uppercase">
                2026 Updated Guide
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white leading-[1.05] mb-5">
              Study in <span className="text-[#FF1F2D]">Canada</span> 2026
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-200 mt-3">— Complete Guide for Indian Students</span>
            </h1>

            <p className="text-base lg:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-2xl">
              World-class education, up to a 3-year PGWP, and the world’s clearest PR pathway via
              Express Entry & PNP. Start your Canada journey with Zafco’s certified counsellors.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link
                to="/study-abroad/book-counselling/?source=canada-hub-hero"
                className="inline-flex items-center justify-center gap-2 bg-[#FF1F2D] hover:bg-[#E10E1F] text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#FF1F2D]/40"
                data-testid="canada-hero-counselling-cta"
              >
                Book Free Counselling
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/919990533327"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setWhatsAppHover(true)}
                onMouseLeave={() => setWhatsAppHover(false)}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5 backdrop-blur-sm"
                data-testid="canada-hero-whatsapp-cta"
              >
                <MessageCircle className={`w-4 h-4 transition-transform ${whatsAppHover ? 'scale-110' : ''}`} />
                WhatsApp: +91-9990533327
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-300/80">
              {['ICCRC-Aligned Consultancy', '23+ Years Experience', '10,000+ Placements'].map((b) => (
                <div key={b} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#FF1F2D]" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative bg-black/30 backdrop-blur-md border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold font-syne text-[#FF1F2D]">{s.val}</div>
                <div className="text-[11px] text-slate-300 mt-0.5 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ 2024-2026 RULES ALERT ============================ */}
      <section className="bg-[#FFF8F0] border-b border-amber-200" data-testid="canada-rules-alert">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-bold tracking-[0.18em] text-amber-700 uppercase mb-1">
                Important · 2024–2026 Updates
              </div>
              <h2 className="text-lg lg:text-xl font-bold text-slate-900 mb-2">
                Canada GIC raised to <span className="text-[#FF1F2D]">CAD 20,635</span> · Spouse work permit rules tightened
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed">
                As of January 2024, proof of funds for cost of living jumped from CAD 10,000 to{' '}
                <strong>CAD 20,635 (~₹12.5 Lakhs)</strong> per single applicant. Spouse Open Work
                Permits are now restricted to spouses of master’s, doctoral, and select professional
                programs.{' '}
                <Link to="/study-abroad/canada/student-visa/" className="text-[#003087] font-semibold underline decoration-[#FF1F2D] underline-offset-4">
                  Read the full updated visa guide →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ WHY CANADA ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="canada-why-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 01 · The Opportunity
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-[#001F5C] leading-tight mb-5">
              Why study in Canada?
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              Canada is the #1 destination for{' '}
              <strong className="text-slate-900">180,000+ Indian students</strong> in 2026. It
              combines world-class universities with the clearest PR pathway of any major study
              destination — and offers up to a{' '}
              <Link to="/study-abroad/careers/canada/pr-after-study/" className="text-[#003087] font-semibold underline decoration-[#FF1F2D] underline-offset-4">
                3-year Post-Graduation Work Permit
              </Link>{' '}
              with no job-offer requirement.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[#001F5C] text-white">
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase w-1/3">Factor</th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase">Why It Matters</th>
                  </tr>
                </thead>
                <tbody>
                  {keyReasons.map((r, i) => (
                    <tr key={r.factor} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#FF1F2D]/10 flex items-center justify-center shrink-0">
                            <r.icon className="w-4 h-4 text-[#FF1F2D]" />
                          </div>
                          <span className="font-bold text-slate-900 text-sm">{r.factor}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{r.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-slate-200">
              {keyReasons.map((r) => (
                <div key={r.factor} className="p-5 bg-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-[#FF1F2D]/10 flex items-center justify-center">
                      <r.icon className="w-4 h-4 text-[#FF1F2D]" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm">{r.factor}</h3>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pl-12">{r.details}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image break */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={IMG_VANCOUVER}
          alt="Vancouver harbour with seaplane and downtown towers"
          loading="lazy"
          width={1600}
          height={600}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/70 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-white/95 text-xl md:text-3xl font-bold font-syne max-w-md leading-snug">
              From Toronto to Vancouver — Canada is your launchpad to PR.
            </p>
          </div>
        </div>
      </div>

      {/* ============== EDUCATION SYSTEM & ELIGIBILITY ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="canada-education-system">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 02 · Education System
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Education System &amp; Eligibility
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Canada offers two distinct pathways — <strong>Universities</strong> for research
              and traditional degrees, and <strong>Colleges</strong> for career-focused, PR-aligned
              graduate certificates. Both are PGWP-eligible if the institution is a DLI.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-5 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#003087]" />
                Levels of Study
              </h3>
              <ul className="space-y-4">
                <li className="border-l-4 border-[#003087] pl-4">
                  <div className="font-bold text-slate-900">Undergraduate (Bachelor’s)</div>
                  <p className="text-sm text-slate-600 mt-1">3–4 years · Eligibility: 12th pass with 60–70%</p>
                </li>
                <li className="border-l-4 border-[#FF1F2D] pl-4 bg-[#FF1F2D]/5 py-3 -mx-1 px-4 rounded-r-lg">
                  <div className="font-bold text-slate-900 flex items-center gap-2">
                    Graduate Certificate / Diploma
                    <span className="text-[10px] uppercase bg-[#FF1F2D] text-white font-bold px-2 py-0.5 rounded-full">Fastest PR</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1">
                    1–2 years at top colleges (Seneca, Humber, Conestoga). Career-focused, PGWP-eligible,
                    and the fastest pathway to PR for working professionals.
                  </p>
                </li>
                <li className="border-l-4 border-[#C5A028] pl-4 bg-[#C5A028]/5 py-3 -mx-1 px-4 rounded-r-lg">
                  <div className="font-bold text-slate-900 flex items-center gap-2">
                    Master’s (Postgraduate)
                    <span className="text-[10px] uppercase bg-[#C5A028] text-black font-bold px-2 py-0.5 rounded-full">High PR Points</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1">
                    1.5–2 years · Bachelor’s 70%+ required. Gets you 3-year PGWP + max CRS points.
                  </p>
                </li>
                <li className="border-l-4 border-slate-300 pl-4">
                  <div className="font-bold text-slate-900">PhD / Doctoral</div>
                  <p className="text-sm text-slate-600 mt-1">3–5 years · Spouse Work Permit + Vanier scholarship eligible</p>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-5 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#003087]" />
                English Proficiency Requirements
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-2 font-bold text-slate-900">Test</th>
                      <th className="text-left py-2 font-bold text-slate-900">SDS Stream</th>
                      <th className="text-left py-2 font-bold text-slate-900">Non-SDS</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    <tr className="border-b border-slate-100 bg-[#FF1F2D]/5">
                      <td className="py-3 font-semibold">IELTS Academic</td>
                      <td className="py-3">6.0 (each band)</td>
                      <td className="py-3">6.5 overall</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 font-semibold">PTE Academic</td>
                      <td className="py-3">60+</td>
                      <td className="py-3">54–65</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 font-semibold">CELPIP</td>
                      <td className="py-3">7+</td>
                      <td className="py-3">7 overall</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-semibold">TOEFL iBT</td>
                      <td className="py-3">N/A</td>
                      <td className="py-3">83–94</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                <strong>CELPIP</strong> is uniquely valid for BOTH Study and PR applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== TOP COURSES & SALARIES ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="canada-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 03 · Career ROI
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Top Courses &amp; 2026 Salary Estimates
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              These three streams dominate Canada’s category-based Express Entry draws.
              STEM and Healthcare receive priority ITAs (Invitations to Apply) for PR.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {topCourses.map((c) => (
              <div
                key={c.title}
                className={`bg-gradient-to-br ${c.accent} border rounded-2xl p-7 hover:shadow-xl hover:-translate-y-1 transition-all group`}
              >
                <div className="text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-2">{c.category}</div>
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5 text-[#003087]" />
                </div>
                <h3 className="text-xl font-bold font-syne text-slate-900 mb-2">{c.title}</h3>
                <div className="text-[#001F5C] font-bold text-sm mb-3 flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  {c.salary} / year
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{c.desc}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-1.5 text-[#003087] font-semibold text-xs hover:gap-2 transition-all"
                >
                  {c.linkLabel} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image break */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={IMG_MONTREAL}
          alt="Montreal old town historic buildings and cobblestones"
          loading="lazy"
          width={1600}
          height={500}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001F5C]/85 via-transparent to-transparent" />
      </div>

      {/* ============== COST OF STUDYING & FINANCIAL PROOF ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="canada-cost">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 04 · Investment
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Cost of Studying &amp; Financial Proof (2026)
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Canada requires <strong>proof of funds</strong> for both tuition AND cost of living
              via the mandatory GIC purchase.
            </p>
          </div>

          {/* CRITICAL ALERT */}
          <div className="mb-8 bg-gradient-to-r from-amber-50 to-amber-100/50 border-l-4 border-amber-500 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-amber-700 uppercase mb-1">
                  2024 Rule Update
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  GIC raised to CAD 20,635 (~₹12.5 Lakhs)
                </h3>
                <p className="text-sm text-slate-700">
                  Proof of funds for living costs has doubled from January 2024. This is on TOP
                  of your first-year tuition fee and must sit in your account / GIC for at least
                  one month before applying.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {[
              { label: 'GIC (Mandatory)', value: 'CAD 20,635', sub: '~₹12.5 L', icon: Shield },
              { label: 'Tuition: Bachelor’s', value: '₹10 – 20 L /yr', sub: 'University', icon: GraduationCap },
              { label: 'Tuition: Master’s', value: '₹15 – 25 L /yr', sub: 'University', icon: Building2 },
              { label: 'Tuition: Diploma', value: '₹8 – 15 L /yr', sub: 'College / Cert', icon: BookOpen },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
                <c.icon className="w-6 h-6 text-[#FF1F2D] mx-auto mb-2" />
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{c.label}</div>
                <div className="text-xl font-bold font-syne text-[#001F5C]">{c.value}</div>
                <div className="text-xs text-slate-500 mt-1">{c.sub}</div>
              </div>
            ))}
          </div>

          <div
            className="rounded-2xl p-7 lg:p-9 text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 60%, #7A0E1A 100%)' }}
          >
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#FF1F2D]/30 rounded-full blur-[100px]" />
            <div className="relative grid md:grid-cols-2 gap-5">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#FF6166] uppercase mb-2">2-Year Master’s Budget</div>
                <div className="text-3xl lg:text-4xl font-bold font-syne mb-1">₹47 – 93 Lakhs</div>
                <p className="text-xs text-slate-300">Tuition + GIC + living + visa + buffer</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#FF6166] uppercase mb-2">1-Year Grad Certificate Budget</div>
                <div className="text-3xl lg:text-4xl font-bold font-syne mb-1">₹21 – 33 Lakhs</div>
                <p className="text-xs text-slate-300">Best ROI · PGWP eligible · PR-aligned</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PGWP & PR PATHWAY ============== */}
      <section className="py-16 lg:py-24 bg-[#001F5C] text-white relative overflow-hidden" data-testid="canada-pr-pathway">
        <div className="absolute top-20 left-[-100px] w-96 h-96 bg-[#FF1F2D]/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-[-100px] w-80 h-80 bg-[#003087] rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF6166] uppercase mb-3">
              Section 05 · The Bridge to PR
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne leading-tight mb-3">
              PGWP &amp; PR Pathways — From Student to PR
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              Canada is unique: your study permit is the first step in a clear,
              government-published roadmap to Permanent Residency.
            </p>
          </div>

          {/* Horizontal pathway */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
            {[
              { n: '1', title: 'Study', desc: '1–2 year program at a DLI', icon: GraduationCap, color: '#FF1F2D' },
              { n: '2', title: 'PGWP', desc: 'Up to 3-year open work permit', icon: Briefcase, color: '#C5A028' },
              { n: '3', title: 'Work Experience', desc: '1 year of skilled Canadian work (NOC TEER 0/1/2/3)', icon: TrendingUp, color: '#0EA5E9' },
              { n: '4', title: 'PR — Express Entry / PNP', desc: 'CEC or PNP nomination → PR in 6–8 months', icon: Shield, color: '#10B981' },
            ].map((p, i) => (
              <div key={p.title} className="relative">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 h-full">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg font-syne mb-3"
                    style={{ background: p.color, color: i === 1 ? '#000' : '#fff' }}
                  >
                    {p.n}
                  </div>
                  <h3 className="text-base font-bold font-syne mb-1.5">{p.title}</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">{p.desc}</p>
                </div>
                {i < 3 && (
                  <ArrowRight className="hidden md:block w-5 h-5 text-[#FF6166] absolute top-1/2 -right-3 transform -translate-y-1/2 z-10" />
                )}
              </div>
            ))}
          </div>

          {/* Category-based callout */}
          <div className="bg-gradient-to-r from-[#FF1F2D]/15 to-[#C5A028]/10 border border-[#FF6166]/30 rounded-2xl p-6 lg:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex items-start gap-4">
                <Award className="w-10 h-10 text-[#FF6166] shrink-0" />
                <div>
                  <h3 className="text-lg lg:text-xl font-bold font-syne mb-1">
                    Category-Based Express Entry Draws
                  </h3>
                  <p className="text-sm text-slate-200 max-w-2xl">
                    IRCC now prioritises <strong className="text-[#FF6166]">STEM, Healthcare, Trades and French-speakers</strong> in category-based draws. These have CRS cutoffs 50–150 points LOWER than general draws.
                  </p>
                </div>
              </div>
              <Link
                to="/study-abroad/careers/canada/best-courses-for-pr/"
                className="inline-flex items-center gap-2 bg-[#FF1F2D] hover:bg-[#E10E1F] text-white font-bold text-sm px-6 py-3 rounded-full transition-all shrink-0"
              >
                See Best Courses for PR <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== TOP UNIVERSITIES & COLLEGES ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="canada-universities">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 06 · Where to Study
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Top Universities &amp; Colleges
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              <strong>Universities</strong> = degrees (B.Sc., M.A., MBA). <strong>Colleges</strong> = 1–2 year graduate certificates / diplomas (career-focused, PR-aligned). Both are PGWP-eligible.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Tier 1 */}
            <div className="bg-white border-2 border-[#FF1F2D]/20 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold font-syne text-slate-900">Top Tier — Universities</h3>
                <span className="text-[10px] uppercase bg-[#FF1F2D] text-white font-bold px-2 py-0.5 rounded-full">Global Top 100</span>
              </div>
              <div className="space-y-3">
                {topUniversities.tier1.map((u) => (
                  <div key={u.name} className="border-l-2 border-[#FF1F2D]/40 pl-4 py-1">
                    <div className="text-sm font-bold text-slate-900">{u.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" /> {u.city} · <span className="text-[#FF1F2D]">{u.rank}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tier 2 */}
            <div className="bg-white border-2 border-[#003087]/20 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold font-syne text-slate-900">Mid-Tier — Universities</h3>
                <span className="text-[10px] uppercase bg-[#003087] text-white font-bold px-2 py-0.5 rounded-full">Strong ROI</span>
              </div>
              <div className="space-y-3">
                {topUniversities.tier2.map((u) => (
                  <div key={u.name} className="border-l-2 border-[#003087]/40 pl-4 py-1">
                    <div className="text-sm font-bold text-slate-900">{u.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" /> {u.city} · <span className="text-[#003087]">{u.rank}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Colleges */}
            <div className="bg-gradient-to-br from-[#C5A028]/10 to-white border-2 border-[#C5A028]/40 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold font-syne text-slate-900">Top Colleges — Certificates</h3>
                <span className="text-[10px] uppercase bg-[#C5A028] text-black font-bold px-2 py-0.5 rounded-full">Fastest PR</span>
              </div>
              <div className="space-y-3">
                {topUniversities.colleges.map((u) => (
                  <div key={u.name} className="border-l-2 border-[#C5A028]/60 pl-4 py-1">
                    <div className="text-sm font-bold text-slate-900">{u.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" /> {u.city} · <span className="text-[#C5A028]">{u.rank}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-600 mt-4 italic">
                Colleges = career-focused, PGWP-eligible, and the most efficient route to PR.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/study-abroad/universities/canada/"
              className="inline-flex items-center gap-2 text-[#003087] font-semibold text-sm hover:gap-3 transition-all"
            >
              View All Canadian Universities & Colleges <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== VISA PROCESS – VERTICAL STEPPER ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="canada-visa-process">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 07 · Visa Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Canada Study Permit Process — 6 Steps
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              SDS (Student Direct Stream) processing for Indian students: 4–6 weeks. Non-SDS: 8–12 weeks.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <ol className="relative border-l-2 border-[#FF1F2D]/40 ml-4 space-y-8">
              {visaSteps.map((step) => (
                <li key={step.n} className="ml-8 relative">
                  <div className="absolute -left-[3.25rem] w-12 h-12 rounded-full bg-[#FF1F2D] text-white font-bold text-lg flex items-center justify-center font-syne shadow-lg ring-4 ring-white">
                    {step.n}
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#FF1F2D]/40 hover:shadow-md transition-all">
                    <h3 className="text-base font-bold font-syne text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/study-abroad/canada/sds-visa/"
              className="inline-flex items-center gap-2 bg-[#FF1F2D] hover:bg-[#E10E1F] text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all"
            >
              Full SDS Visa Guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== QUICK NAV / RESOURCE LINKS ============== */}
      <section className="py-16 bg-white" data-testid="canada-quick-links">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 08 · Resource Hub
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight">
              Deep-Dive into Every Canada Topic
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="bg-white rounded-xl p-5 flex items-start gap-4 group border border-slate-200 hover:border-[#FF1F2D] hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#FF1F2D]/10 flex items-center justify-center shrink-0 group-hover:bg-[#FF1F2D] transition-colors">
                  <link.icon className="w-5 h-5 text-[#FF1F2D] group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#003087] transition-colors">
                    {link.label}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#FF1F2D] mt-1 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Image break */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={IMG_CAMPUS}
          alt="Canadian university campus in autumn with maple leaves"
          loading="lazy"
          width={1600}
          height={500}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#001F5C]/80 to-transparent" />
      </div>

      {/* ============== COMPARISONS ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="canada-comparisons">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 09 · Compare Destinations
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Canada vs Other Destinations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                headline: 'Canada vs UK',
                insight: "Canada offers a clearer PR pathway via Express Entry; UK offers faster 1-year master's degrees but no automatic PR route.",
                link: '/study-abroad/comparison/canada-vs-uk-for-pr/',
                cta: 'Read Canada vs UK PR breakdown',
              },
              {
                headline: 'UK vs Australia (MBA)',
                insight: 'For MBA aspirants weighing Canada — explore the UK vs Australia MBA route comparison here.',
                link: '/study-abroad/comparison/uk-vs-australia-for-mba/',
                cta: 'Read UK vs Australia MBA guide',
              },
            ].map((c) => (
              <div key={c.headline} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#FF1F2D] hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FF1F2D]/10 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#FF1F2D]" />
                  </div>
                  <h3 className="text-lg font-bold font-syne text-slate-900">{c.headline}</h3>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{c.insight}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-1.5 text-[#003087] font-semibold text-xs hover:gap-2 transition-all"
                >
                  {c.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== SMART PROFILE-EVAL CTA ============== */}
      <SmartProfileEvalCTA country="Canada" country_slug="canada-hub" testId="canada-hub-smart-cta" />

      {/* ============== FAQ ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="canada-faq">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-10 text-center">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#FF1F2D] uppercase mb-3">
              Section 10 · FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600">
              The 20 most-asked questions by Indian students applying to Canada in 2026 — including
              GIC, family, and best courses for PR.
            </p>
          </div>

          <Accordion type="single" collapsible className="rounded-2xl bg-slate-50 border border-slate-200 px-6 divide-y divide-slate-200">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="border-b-0"
                data-testid={`canada-faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold text-slate-900 hover:no-underline hover:text-[#FF1F2D] py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-700 leading-relaxed pb-5 pt-0">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============== FINAL CTA / SERVICES ============== */}
      <section
        className="relative py-20 overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 60%, #7A0E1A 100%)' }}
        data-testid="canada-final-cta"
      >
        <div className="absolute top-0 right-[-80px] w-[420px] h-[420px] bg-[#FF1F2D]/25 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-[-80px] w-80 h-80 bg-[#003087] rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 text-[10.5px] font-bold tracking-[0.22em] text-[#FF6166] uppercase">
            <Star className="w-3.5 h-3.5" />
            Ready to Apply?
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne mb-4 leading-tight">
            Start your Canada journey with Zafco
          </h2>
          <p className="text-base lg:text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Delhi-based verified consultancy. From college shortlisting to GIC, SDS visa, and PR
            planning — we’re with you every step of the way.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <a
              href="https://wa.me/919990533327?text=Hi%20Zafco%2C%20I%27d%20like%20to%20discuss%20studying%20in%20Canada."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#FF1F2D] hover:bg-[#E10E1F] text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              data-testid="canada-final-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp: +91-9990533327
            </a>
            <a
              href="mailto:studyabroad@zafco.in"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5"
              data-testid="canada-final-email"
            >
              <Mail className="w-4 h-4" />
              studyabroad@zafco.in
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left max-w-3xl mx-auto">
            {[
              { t: 'College / University Selection', icon: Building2 },
              { t: 'SDS Visa Filing', icon: Shield },
              { t: 'GIC Coordination', icon: DollarSign },
              { t: 'PR Pathway Planning', icon: TrendingUp },
            ].map((s) => (
              <div key={s.t} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <s.icon className="w-5 h-5 text-[#FF6166] mb-2" />
                <div className="text-sm font-bold text-white">{s.t}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5 inline mr-1.5" />
            Delhi office hours: Mon–Sat · 10am–7pm IST · Verified MEA-aligned consultancy
          </div>
        </div>
      </section>

      {/* ============== STICKY MOBILE CTA ============== */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#001F5C]/95 backdrop-blur-md border-t border-[#FF1F2D]/30 px-3 py-2.5 flex gap-2"
        data-testid="canada-sticky-mobile-cta"
      >
        <a
          href="https://wa.me/919990533327"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-slate-900 text-xs font-bold px-3 py-2.5 rounded-full"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp Us
        </a>
        <Link
          to="/study-abroad/book-counselling/?source=canada-sticky"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#FF1F2D] text-white text-xs font-bold px-3 py-2.5 rounded-full"
        >
          Free Counselling
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default CanadaHubPage;
